import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { pollAPI, entityAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Vote,
  Plus,
  ThumbsUp,
  Users,
  Bell,
  Clock,
  CheckCircle,
  TrendingUp,
  Loader2
} from 'lucide-react';

export default function RuchiBoxPoll() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [voteLoading, setVoteLoading] = useState(null);
  const [entityName, setEntityName] = useState('');
  const [threshold, setThreshold] = useState(5);
  
  const [newPoll, setNewPoll] = useState({
    title: '',
    description: '',
    quantity_needed: ''
  });

  useEffect(() => {
    if (user?.entity_id) {
      loadPolls();
      loadEntityName();
    }
  }, [user?.entity_id]);

  const loadEntityName = async () => {
    try {
      const response = await entityAPI.getAll();
      const entity = response.data.find(e => e.id === user.entity_id);
      if (entity) {
        setEntityName(entity.name);
      }
    } catch (error) {
      console.error('Failed to load entity name');
    }
  };

  const loadPolls = async () => {
    setLoading(true);
    try {
      const response = await pollAPI.getAll(user.entity_id, user.id);
      setPolls(response.data);
    } catch (error) {
      toast.error('Failed to load polls');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePoll = async () => {
    if (!newPoll.title.trim() || !newPoll.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreateLoading(true);
    try {
      await pollAPI.create(newPoll, user.id);
      toast.success('Poll created! Your vote has been counted.');
      setShowCreateModal(false);
      setNewPoll({ title: '', description: '', quantity_needed: '' });
      loadPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create poll');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleVote = async (pollId, hasVoted) => {
    setVoteLoading(pollId);
    try {
      if (hasVoted) {
        await pollAPI.removeVote(pollId, user.id);
        toast.success('Vote removed');
      } else {
        const result = await pollAPI.vote(pollId, user.id);
        if (result.data.threshold_reached) {
          toast.success('Threshold reached! Vendors have been notified 🎉');
        } else {
          toast.success('Vote recorded!');
        }
      }
      loadPolls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    } finally {
      setVoteLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'threshold_reached':
        return <Badge className="bg-green-500">Threshold Reached!</Badge>;
      case 'fulfilled':
        return <Badge className="bg-blue-500">Fulfilled</Badge>;
      case 'closed':
        return <Badge variant="secondary">Closed</Badge>;
      default:
        return <Badge variant="outline">Open</Badge>;
    }
  };

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <header className="glass-header sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button variant="ghost" onClick={() => navigate('/')} className="pl-0">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                  <Vote className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading text-xl font-bold">RuchiBox Poll</span>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Vote className="w-16 h-16 text-purple-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-4">Sign in to Participate</h2>
          <p className="text-muted-foreground mb-6">Create and vote on community polls after signing in.</p>
          <Button onClick={() => navigate('/auth')} className="rounded-full">
            Sign In / Register
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/')} className="pl-0">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                <Vote className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">RuchiBox Poll</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Vote className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Community Bulk Orders
          </h1>
          <p className="text-muted-foreground">
            {entityName && `Polls for ${entityName}`}
          </p>
        </div>

        {/* How It Works */}
        <Card className="mb-6 border-purple-100">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                  <Plus className="w-5 h-5 text-purple-500" />
                </div>
                <p className="font-medium">Create Poll</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <p className="font-medium">{threshold} Votes Needed</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-2">
                  <Bell className="w-5 h-5 text-purple-500" />
                </div>
                <p className="font-medium">Vendors Notified</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Poll Button */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="w-full h-14 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Poll
          </Button>
        </div>

        {/* Polls List */}
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            Active Polls ({polls.filter(p => p.status === 'open').length})
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
              <p className="text-muted-foreground mt-2">Loading polls...</p>
            </div>
          ) : polls.length === 0 ? (
            <Card className="border-dashed border-2 border-purple-200">
              <CardContent className="py-12 text-center">
                <Vote className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">No polls yet</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Be the first to create a community poll!
                </p>
                <Button onClick={() => setShowCreateModal(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Poll
                </Button>
              </CardContent>
            </Card>
          ) : (
            polls.map(poll => (
              <Card key={poll.id} className={`transition-all ${poll.status === 'threshold_reached' ? 'border-green-300 bg-green-50/50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold">{poll.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{poll.description}</p>
                      {poll.quantity_needed && (
                        <Badge variant="outline" className="mt-2">
                          Quantity: {poll.quantity_needed}
                        </Badge>
                      )}
                    </div>
                    {getStatusBadge(poll.status)}
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">
                        {poll.vote_count} / {threshold} votes
                      </span>
                      <span className="font-medium text-purple-600">
                        {Math.round((poll.vote_count / threshold) * 100)}%
                      </span>
                    </div>
                    <Progress 
                      value={Math.min((poll.vote_count / threshold) * 100, 100)} 
                      className="h-2"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      <Clock className="w-3 h-3 inline mr-1" />
                      By {poll.created_by_name}
                    </div>
                    
                    {poll.status === 'open' && (
                      <Button
                        size="sm"
                        variant={poll.has_voted ? "secondary" : "default"}
                        onClick={() => handleVote(poll.id, poll.has_voted)}
                        disabled={voteLoading === poll.id || poll.created_by_id === user.id}
                        className="rounded-full"
                      >
                        {voteLoading === poll.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : poll.has_voted ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Voted
                          </>
                        ) : (
                          <>
                            <ThumbsUp className="w-4 h-4 mr-1" />
                            Vote
                          </>
                        )}
                      </Button>
                    )}
                    
                    {poll.status === 'threshold_reached' && (
                      <Badge className="bg-green-500 animate-pulse">
                        <Bell className="w-3 h-3 mr-1" />
                        Vendors Notified!
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Create Poll Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-500" />
              Create Community Poll
            </DialogTitle>
            <DialogDescription>
              Start a poll for bulk orders. Once it reaches {threshold} votes, vendors will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="poll-title">What do you need?</Label>
              <Input
                id="poll-title"
                placeholder="e.g., Cold-pressed groundnut oil"
                value={newPoll.title}
                onChange={(e) => setNewPoll(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="poll-desc">Details</Label>
              <Textarea
                id="poll-desc"
                placeholder="e.g., Looking for organic, cold-pressed oil from a reliable source"
                value={newPoll.description}
                onChange={(e) => setNewPoll(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="poll-qty">Quantity (optional)</Label>
              <Input
                id="poll-qty"
                placeholder="e.g., 1 litre, 5 kg"
                value={newPoll.quantity_needed}
                onChange={(e) => setNewPoll(prev => ({ ...prev, quantity_needed: e.target.value }))}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-purple-500 hover:bg-purple-600"
                onClick={handleCreatePoll}
                disabled={createLoading}
              >
                {createLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Vote className="w-4 h-4 mr-2" />
                )}
                Create Poll
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
